import { EnvironmentService } from '../services/environment.service';
import { Environment } from '../models/environment';
import {
  Observable,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
  tap,
  map,
  catchError,
  of,
  forkJoin,
  throwError,
  finalize,
} from 'rxjs';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ReleaseReportService } from '../services/releasereport.service';
import {
  Release,
  ReleaseReportReleasesResponse,
  ReviewClassification
} from '../models/release';
import { state, style, trigger } from '@angular/animations';
import { SelectionModel } from '@angular/cdk/collections';
import { SafeHtml } from '@angular/platform-browser';
import { AdoAccount } from '../models/ado-account';
import { AdoOrg } from '../models/ado-org';
import { AdoUserProfile } from '../models/ado-user-profile';
import { RcjitService } from '../rcjit/rcjit.service';
import { EditReleaseDialogComponent } from '../dialog/edit-release/edit-release-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ReleaseDetail } from '../models/release-detail';
import { AgGridAngular } from 'ag-grid-angular';
import type {
  ColDef,
  RowSelectionOptions,
  GetRowIdFunc,
  GetRowIdParams,
  RowClassRules,
} from 'ag-grid-enterprise';
import { themeQuartz } from 'ag-grid-community';
import { ReleaseSuccessFailureRenderer } from './release-success-failure-renderer';
import { ReviewClassificationDisplayPipe } from '../pipes/review-classification-display.pipe';
import { ActivatedRoute } from '@angular/router';
import { UserProfileService } from '../services/user-profile.service';
import { OwnershipEntry } from '../models/ownership-entry';
import { LoadingService } from '../services/loading.service';
import { CacheService } from '../services/cache.service';
import { ReleasesDataService } from '../services/releases-data.service';

@Component({
  selector: 'app-releases',
  template: `<app-header></app-header>
    <div class="releases-title-bar">
      <mat-form-field
        appearance="outline"
        class="window-select"
        subscriptSizing="dynamic"
      >
        <mat-label>Time range</mat-label>
        <mat-select
          [value]="releaseWindowDays"
          (selectionChange)="onWindowChange($event.value)"
        >
          @for (opt of windowOptions; track opt.days) {
            <mat-option [value]="opt.days">{{ opt.label }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
      <h1 class="reportTitle">Releases</h1>
      <button
        mat-stroked-button
        class="retro-button"
        matTooltip="Releases grouped by team — for retros and updating"
        aria-label="Retro mode"
        [disabled]="releases.length === 0"
        routerLink="/releases/retro"
      >
        <mat-icon>groups</mat-icon>
        Retro mode
      </button>
    </div>

    <div class="grid-container">
      @if (selectedTeam && selectedTeam.length > 0) {
        <button mat-raised-button (click)="selectedTeam = ''">Back</button>
        <app-releases-by-team
          [team]="selectedTeam"
          [releases]="releases"
        ></app-releases-by-team>
      } @else {
        <ag-grid-angular
          [ngClass]="{ 'ag-grid-angular': true }"
          [rowData]="releases"
          [columnDefs]="agGridColumns"
          [defaultColDef]="defaultColDef"
          [pagination]="true"
          [paginationPageSize]="20"
          [animateRows]="true"
          [enableCellTextSelection]="true"
          [rowSelection]="rowSelection"
          [theme]="theme"
          [rowHeight]="48"
          [headerHeight]="48"
          [getRowId]="getRowId"
          [rowClassRules]="rowClassRules"
        ></ag-grid-angular>
      }
    </div>
    `,
  styleUrls: ['../home/home.component.css'],
  styles: [
    `
      .highlight-row-red {
        background-color: #ffdddd;
      }

      .highlight-row-green {
        background-color: #ddffdd;
      }

      .reportTitle {
        font: 750 2vw/4vw Roboto, Helvetica Neue, Helvetica, Arial, sans-serif;
        font-weight: bold;
        color: #0093a1;
        text-align: center;
        margin-bottom: 0%;
        margin: 16px 0;
      }

      .releases-title-bar {
        display: grid;
        grid-template-columns: 1fr auto 1fr;
        align-items: center;
      }

      .releases-title-bar .reportTitle {
        grid-column: 2;
      }

      .releases-title-bar .window-select {
        grid-column: 1;
        justify-self: start;
        margin-left: 16px;
        width: 160px;
      }

      .releases-title-bar .retro-button {
        grid-column: 3;
        justify-self: end;
        margin-right: 16px;
      }

      .releases-title-bar .retro-button mat-icon {
        margin-right: 4px;
      }

      .grid-container {
        flex: 1;
        display: flex;
        flex-direction: column;
        min-height: 0;
        margin: 16px 0;
        height: calc(100vh - 220px);
      }

      .ag-grid-angular {
        width: 100%;
        height: 100%;
      }

      .ag-cell-content-centered mat-icon {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100%;
        width: 100%;
      }

      .ag-cell-content-centered {
        display: flex;
        justify-content: center;
        align-items: center;
      }

      /* Review Classification Cell Styles */
      .review-classification-cell {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 4px 8px;
        border-radius: 4px;
        transition: background-color 0.2s ease;
        cursor: pointer;
        height: 100%;
        gap: 8px;
      }

      .review-classification-cell:hover {
        background-color: rgba(0, 147, 161, 0.1);
      }

      .review-classification-cell .review-text {
        color: #333;
        white-space: nowrap;
      }

      .review-classification-cell i.edit-icon {
        opacity: 0.6;
        color: #00a103;
        transition: opacity 0.2s ease;
        min-width: 18px;
      }

      .review-classification-cell:hover .edit-icon {
        opacity: 1;
      }

      :host ::ng-deep .ag-header-cell-text {
        justify-content: center !important;
        text-align: center;
        width: 100%;
      }
    `,
  ],
  animations: [
    trigger('detailExpand', [
      state(
        'collapsed',
        style({ height: '0px', minHeight: '0', display: 'none' })
      ),
      state('expanded', style({ height: '*' })),
    ]),
  ],
  standalone: false,
})
export class ReleasesComponent implements OnInit, OnDestroy {
  private readonly reviewClassificationPipe =
    new ReviewClassificationDisplayPipe();

  public releaseSuccessFailureRenderer: any = ReleaseSuccessFailureRenderer;

  public theme = themeQuartz.withParams({});
  @ViewChild(AgGridAngular) agGrid!: AgGridAngular;
  agGridColumns: ColDef[] = [
    {
      field: 'releaseStartedDate',
      headerName: 'Release Date',
      sortable: true,
      filter: 'agDateColumnFilter',
      filterParams: {},
      flex: 1.2,
      valueFormatter: (params) => {
        if (params.value) {
          return this.getDisplayDate({ ...params.data });
        }
        return '';
      },
      headerClass: 'ag-header-cell-label',
      cellClass: 'ag-cell-content',
      cellRenderer: 'agGroupCellRenderer',
    },
    {
      field: 'pipelineName',
      headerName: 'Pipeline Name',
      sortable: true,
      filter: true,
      flex: 1.5,
      headerClass: 'ag-header-cell-label',
      cellClass: 'ag-cell-content',
      tooltipField: 'pipelineName',
    },
    {
      field: 'releaseName',
      headerName: 'Release Name',
      sortable: true,
      filter: true,
      flex: 1,
      headerClass: 'ag-header-cell-label',
      cellClass: 'ag-cell-content',
      tooltipField: 'releaseName',
      cellRenderer: (params: any) => {
        return ` <a
          style="text-decoration: underline; color: blue"
          target="_blank"
          href="${params.data.releaseLink}"
          rel="noopener noreferrer"
        >
          ${params.data.releaseName}
        </a>`;
      },
    },
    {
      field: 'team',
      headerName: 'Team',
      sortable: true,
      filter: 'agMultiColumnFilter',
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        suppressSelectAll: false,
        suppressMiniFilter: false,
        miniFilterPlaceholder: 'Search teams...',
      },
      flex: 1,
      headerClass: 'ag-header-cell-label',
      cellClass: 'ag-cell-content',
      cellStyle: { cursor: 'pointer' },
      cellRenderer: (params: any) => {
        if (!params.value) return '';
        return `
          <div style="display: flex; align-items: center; gap: 4px;">
            <i class="material-icons" style="color: #0093a1; font-size: 18px; min-width: 18px;">info</i>
            <span style="margin-left: 4px">${params.value}</span>
          </div>
        `;
      },
      onCellClicked: (params: any) => {
        this.selectedTeam = params.data.team;
      }
    },
    {
      field: 'branchName',
      headerName: 'Branch',
      sortable: true,
      filter: 'agMultiColumnFilter',
      flex: 1,
      headerClass: 'ag-header-cell-label',
      cellClass: 'ag-cell-content',
      tooltipField: 'branchName',
    },
    {
      field: 'releaseStatus',
      headerName: 'Pipeline Status',
      sortable: true,
      filter: true,
      flex: 0.8,
      headerClass: 'ag-header-cell-label',
      cellClass: 'ag-cell-content-centered',
      cellRenderer: ReleaseSuccessFailureRenderer,
      cellRendererParams: {
        booleanPropertyName: 'releaseStatus',
      },
    },
    {
      field: 'changeFail',
      headerName: 'Change Fail',
      sortable: true,
      filter: true,
      flex: 0.8,
      headerClass: 'ag-header-cell-label',
      cellClass: 'ag-cell-content-centered',
      cellRenderer: ReleaseSuccessFailureRenderer,
      cellRendererParams: {
        booleanPropertyName: 'changeFail',
      },
    },
    {
      field: 'isTransient',
      headerName: 'Transient',
      sortable: true,
      filter: true,
      flex: 0.8,
      headerClass: 'ag-header-cell-label',
      cellClass: 'ag-cell-content-centered',
      cellRenderer: ReleaseSuccessFailureRenderer,
      cellRendererParams: {
        booleanPropertyName: 'isTransient',
        isWarning: true,
        invertCondition: true,
        trueIsNull: true,
      },
    },
    {
      field: 'reviewClassification',
      headerName: 'Review Classification',
      cellStyle: {
        cursor: 'pointer',
      },
      sortable: true,
      filter: 'agMultiColumnFilter',
      flex: 0.8,
      headerClass: 'ag-header-cell-label',
      cellClass: 'ag-cell-content-centered',
      onCellClicked: (params: any) => {
        this.openEditReleaseModal(params.data);
      },
      cellRenderer: (params: any) => {
        const classification =
          ReviewClassification[
            params.value as keyof typeof ReviewClassification
          ];
        return `<div class="review-classification-cell">
          <i class="material-icons edit-icon" style="font-size: 18px; margin-right: 8px;color: #00a103">edit</i>
          <span class="review-text">
          ${this.reviewClassificationPipe.transform(classification)}
          </span>
        </div>`;
      },
    },
  ];

  public rowSelection: RowSelectionOptions | 'single' | 'multiple' = {
    mode: 'multiRow',
    checkboxes: true,
    enableClickSelection: true,
  };

  defaultColDef: ColDef = {
    resizable: true,
    sortable: true,
    filter: true,
    floatingFilter: true,
    suppressMovable: true,
    wrapHeaderText: true,
    autoHeaderHeight: true,
    tooltipValueGetter: (params: any) => params.value,
  };

  isAuthenticated: boolean = false;
  isMarketplaceUser: boolean = false;
  releases = new Array<Release>();
  releaseReportReleases: ReleaseReportReleasesResponse[] = [];
  releaseDates: Array<string> = [];
  friendlyDates: Array<string> = [];
  selectedDate: string = '';
  selectedTeam: string = '';
  selectedRetroNote: string = '';
  selectedReleases: Release[] = [];
  loading: boolean = false;
  selection = new SelectionModel<Release>(true, []);
  initialSelection: any = [];
  allowMultiSelect: boolean = true;
  searchTerm: string = '';
  expandedRow: Release | null = null;
  allRowsSelected: boolean = false;
  downloadHref!: SafeHtml;
  environment: Environment;
  gitHubOrg: string | null = null;
  currentUserTeam: string = '';
  ownershipTeam: string = '';
  ownershipTeams: Array<string> = new Array<string>();
  currentUsersMembershipTeams: Array<string> = new Array<string>();
  ownershipAdmin: string = '';
  ownershipAdmins: Array<string> = new Array<string>();
  ownershipRepoId: string = '';
  ownershipRepoUrl: string = '';
  ownershipRepoName: string = 'Ownership';
  ownershipProject: string = 'System';
  membershipRepoName: string = 'Membership';
  membershipFileName: string = 'CarvanaAzureActiveDirectory.json';
  ownershipFileName: string = 'RepoOwnershipMaster.json';
  ownershipData: any[] = [];
  availableAdoAccounts: Array<AdoAccount>;
  availableOrgs: Array<AdoOrg> = new Array<AdoOrg>();
  defaultOrg?: AdoAccount;
  defaultProject?: string;
  defaultProjectId?: string;
  allProjects: Array<any> = new Array<any>();
  userProfile!: AdoUserProfile;
  onlyShowProdRelease = false;
  pageEnabled: boolean;
  currentPage = 1;
  pageSize = 10;
  private releaseIdParam: string | null = null;

  private readonly _destroying$ = new Subject<void>();
  /** Tracks the active releases load so an edit can cancel an in-flight refresh (prevents a stale
   * revalidate from re-writing the pre-edit snapshot back into the cache). */
  private releaseLoadSub?: Subscription;

  /** Fetch window in days. Defaults small for a fast load; widen via the Time range selector. */
  static readonly DEFAULT_WINDOW_DAYS = 90;
  private static readonly GRID_CACHE_TTL_MS = 12 * 60 * 60 * 1000; // 12h
  releaseWindowDays = ReleasesComponent.DEFAULT_WINDOW_DAYS;
  readonly windowOptions = [
    { label: 'Last 90 days', days: 90 },
    { label: 'Last 6 months', days: 182 },
    { label: 'Last 2 years', days: 730 },
  ];

  constructor(
    private readonly environmentService: EnvironmentService,
    private readonly releaseReportService: ReleaseReportService,
    private readonly jitService: RcjitService,
    private readonly dialog: MatDialog,
    private readonly route: ActivatedRoute,
    private readonly userProfileService: UserProfileService,
    public readonly loadingService: LoadingService,
    private readonly cacheService: CacheService,
    private readonly releasesData: ReleasesDataService
  ) {
    this.environment = this.environmentService.getEnvironment();
    this.pageEnabled = this.environment.featureToggles.releasesEnabled === 'true';
    this.availableAdoAccounts = new Array<AdoAccount>();
    this.gitHubOrg = this.environment.github.org;
  }

  openEditReleaseModal(release: any): void {
    const dialogRef = this.dialog.open(EditReleaseDialogComponent, {
      width: '600px',
      panelClass: 'release-edit-dialog',
      data: { ...release },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const index = this.releases.findIndex((item) => item.id === result.id);
        if (index > -1) {
          // Replace with a NEW array reference so child views bound to [releases] (presentation
          // mode) see the change via ngOnChanges and re-render — e.g. an edited Review
          // classification updates live. The grid updates directly via setData below.
          this.releases = this.releases.map((item, i) => (i === index ? result : item));
          // The grid may not be mounted (team view) — guard the API.
          const rowNode = this.agGrid?.api?.getRowNode(result.id!.toString());
          if (rowNode) {
            rowNode.setData(result);
            this.agGrid.api.refreshCells({ force: true });
          }
        }
        // Cancel any in-flight refresh so a late revalidate can't re-cache the pre-edit snapshot,
        // then write the edit through to the cache (both views) so a reload reflects it.
        this.releaseLoadSub?.unsubscribe();
        this.releasesData.applyEdit(result);
      }
    });
  }

  getRowId: GetRowIdFunc = (params: GetRowIdParams) => {
    return `${params.data.id}`;
  };

  ngOnInit(): void {
    this.userProfileService.isAuthenticated()
      .pipe(takeUntil(this._destroying$))
      .subscribe(isAuthenticated => {
        this.isAuthenticated = isAuthenticated;

        if (isAuthenticated) {
          this.selectedDate = 'Pending';
          this.loading = true;
          this.route.queryParams.subscribe(params => {
            this.releaseIdParam = params['releaseId'];
          });

          this.releaseLoadSub = this.initializeComponent()
            .pipe(
              takeUntil(this._destroying$),
              finalize(() => this.loading = false)
            )
            .subscribe({
              error: (error) => {
                console.error('Error in initialization sequence:', error);
              }
            });
        }
      });
  }

  /**
   * Initializes the application by loading all required data in sequence
   * @returns Observable that completes when all initialization steps are done
   */
  private initializeComponent(): Observable<any> {
    this.loadingService.show('releases');
    return this.loadUserProfile().pipe(
      switchMap((profile) => this.loadUserAccounts(profile.publicAlias)),
      switchMap(() => this.loadAvailableOrgs()),
      switchMap(() => this.loadProjectsForAccounts()),
      tap(() => this.setDefaultOrg()),
      tap(() => this.setDefaultProject()),
      switchMap(() => this.getReleases()),
      finalize(() => this.loadingService.hide('releases'))
    );
  }

  applyFilter(): void {
    if(this.releaseIdParam) {
      let matchingRelease = this.releases.find(r => r.adoReleaseId === this.releaseIdParam);
      if(matchingRelease) {
        // Set first filter
        this.agGrid.api.setColumnFilterModel("pipelineName", {
          values: [matchingRelease.pipelineName]
        });

        // Set second filter
        this.agGrid.api.setColumnFilterModel("releaseName", {
          values: [matchingRelease.releaseName]
        }).then(() => {
          // Apply all filter changes at once
          this.agGrid.api.onFilterChanged();
        });
      }
    }
  }

  /**
   * Loads the current user profile
   * @returns Observable of the user profile
   */
  private loadUserProfile(): Observable<AdoUserProfile> {
    return this.userProfileService.getCurrentProfile().pipe(
      map((profile) => {
        if (!profile) {
          throw new Error('User profile not found');
        }
        this.userProfile = profile;
        return profile;
      })
    );
  }

  /**
   * Loads user accounts based on the public alias
   * @param publicAlias The user's public alias
   * @returns Observable of the accounts response
   */
  private loadUserAccounts(publicAlias: string): Observable<any> {
    return this.userProfileService.getAccounts(publicAlias).pipe(
      tap(accounts => {
        this.availableAdoAccounts = accounts.value;
      }),
      catchError(error => {
        console.error('Error loading user accounts:', error);
        return throwError(() => new Error('Failed to load user accounts'));
      })
    );
  }

  /**
   * Loads available organizations and filters accounts
   * @returns Observable of available orgs
   */
  private loadAvailableOrgs(): Observable<string[]> {
    return this.getAvailableOrgs().pipe(
      catchError(error => {
        console.error('Error loading available orgs:', error);
        return throwError(() => new Error('Failed to load available organizations'));
      })
    );
  }

  /**
   * Loads projects for all available accounts
   * @returns Observable that completes when all projects are loaded
   */
  private loadProjectsForAccounts(): Observable<any[]> {
    if (this.availableAdoAccounts.length === 0) {
      return of([]);
    }

    // Create an array of observables for each account's projects
    const projectObservables = this.availableAdoAccounts.map(account =>
      this.userProfileService.getProjects(account.accountName).pipe(
        tap(projects => {
          // Initialize projects array if needed
          account.projects = account.projects || [];

          // Add project names to the account
          projects.value.forEach((project: any) => {
            account.projects.push(project.name);
            this.allProjects.push(project);
          });

          // If this is the default org, set default project
          if (this.defaultOrg?.accountName === account.accountName) {
            this.defaultOrg.projects = account.projects;
            this.setDefaultProject();
          }
        }),
        catchError(error => {
          console.error(`Error fetching projects for account ${account.accountName}:`, error);
          // Return empty array to continue with other accounts
          return of({ value: [] });
        })
      )
    );

    // Use forkJoin to wait for all project requests to complete
    return forkJoin(projectObservables).pipe(
      // After all projects are fetched, get ownership data
      switchMap(() => {
        // Only proceed with ownership if we have a default org
        if (this.defaultOrg) {
          return this.getOwnership().pipe(
            // After getting ownership, get the current user's team
            switchMap(() => this.getCurrentUsersTeam()),
            // Return the original result to maintain the chain
            map(() => [])
          );
        }
        return of([]);
      }),
      catchError(error => {
        console.error('Error in fetchProjectsForAccounts:', error);
        return throwError(() => new Error('Failed to fetch projects for accounts'));
      })
    );
  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }

  clearDates(): void {
    this.selectedDate = '';
    this.releaseLoadSub = this.getReleases()
      .pipe(takeUntil(this._destroying$))
      .subscribe({
        error: (error) => {
          console.error('Error clearing dates:', error);
        }
      });
  }

  handleDateChange(): void {
    this.releaseLoadSub = this.getReleases()
      .pipe(takeUntil(this._destroying$))
      .subscribe({
        error: (error) => {
          console.error('Error handling date change:', error);
        }
      });
  }

  /**
   * Gets the current user's team from Azure DevOps
   * @returns Observable of team names
   */
  getCurrentUsersTeam(): Observable<string[]> {
    if (!this.gitHubOrg || !this.ownershipProject) {
      console.log('Default org or ownership project not set');
      return of([]);
    }

    return this.userProfileService
      .getAzureDevOpsTeams(this.gitHubOrg)
      .pipe(
        tap(teams => {
          this.currentUsersMembershipTeams = teams;
          this.currentUserTeam = teams[0] ?? '';
        }),
        catchError(error => {
          console.error('Error getting user teams:', error);
          return of([]);
        })
      );
  }

  formatDate(date: Date): string {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    };
    return date.toLocaleDateString('en-US', options);
  }

  /**
   * Gets releases from the release report service
   * @returns Observable that completes when releases are fetched and processed
   */
  getReleases(): Observable<any> {
    this.loadingService.show('releases');
    const now = new Date();
    const startDate = new Date(now);
    startDate.setDate(now.getDate() - this.releaseWindowDays);
    const startIso = startDate.toISOString();
    const endIso = now.toISOString();
    // Keyed only by schema version + window: the 12h TTL and the always-on revalidate bound
    // staleness, so embedding the date just orphaned a fresh key every day (reclaimed only on
    // quota eviction). Matches the retro key shape. 'v2' versions the cached schema so a stale
    // shape from a prior build is never read.
    const cacheKey = this.cacheService.generateCacheKey(ReleasesDataService.GRID_CACHE_PREFIX, [
      'v2',
      String(this.releaseWindowDays),
    ]);

    // Stale-while-revalidate: paint a cached (lightweight) copy instantly, then refresh below.
    const cached = this.cacheService.get<Release[]>(cacheKey, ReleasesComponent.GRID_CACHE_TTL_MS);
    if (Array.isArray(cached)) {
      this.paintReleases(ReleasesDataService.fromCacheProjection(cached));
      this.loadingService.hide('releases');
    } else {
      this.releases = new Array<Release>();
    }

    return this.releaseReportService.getReleases(startIso, endIso)
    .pipe(
      tap((releases) => {
        // Render the full data first; caching is best-effort and must never block the grid.
        this.applyReleases(releases);
        this.cacheService.set(cacheKey, ReleasesDataService.toCacheProjection(this.releases));
      }),
      catchError((error) => {
        console.error('Error loading releases:', error);
        return of([]);
      }),
      finalize(() => this.loadingService.hide('releases'))
    );
  }

  /** Processes the raw release report into the grid and resolves any team-from-URL deep link. */
  private applyReleases(releases: ReleaseReportReleasesResponse[]): void {
    this.releaseReportReleases = releases;
    this.releases = new Array<Release>();
    this.processReleases(releases);
    this.pushToGridAndResolveTeam();
  }

  /** Paints an already-processed (e.g. cached) Release[] into the grid without re-processing. */
  private paintReleases(releases: Release[]): void {
    this.releases = releases;
    this.pushToGridAndResolveTeam();
  }

  private pushToGridAndResolveTeam(): void {
    if (this.agGrid?.api) {
      this.agGrid.api.setGridOption('rowData', this.releases);
    }
    // Check for team parameter after releases are processed
    const teamFromUrl = this.route.snapshot.params['teamname'];
    if (teamFromUrl) {
      const matchingRelease = this.releases.find(r => r.team.toLowerCase() === teamFromUrl.toLowerCase());
      if (matchingRelease) {
        this.selectedTeam = matchingRelease.team;
      }
    }
  }

  /** Changes the fetch window (Time range selector) and reloads. */
  onWindowChange(days: number): void {
    this.releaseWindowDays = days;
    this.releaseLoadSub = this.getReleases()
      .pipe(takeUntil(this._destroying$))
      .subscribe({
        error: (error) => {
          console.error('Error changing release window:', error);
        }
      });
  }

  processReleases(releases: ReleaseReportReleasesResponse[]): void {
    releases?.forEach((release: ReleaseReportReleasesResponse) => {
      try {
        this.releases.push({
          id: release.Release.ReleaseId,
          adoReleaseId: release.Release.AdoReleaseId,
          branchName: release.Release.BranchName,
          pipelineName: release.Release.PipelineName,
          releaseName: release.Release.ReleaseName,
          isYaml: release.Release.IsYaml,
          releaseStartedDate: new Date(release.Release.StartedDateTimeUtc),
          releaseStartedDateLocal: new Date(new Date(release.Release.StartedDateTimeUtc).toLocaleString('en-US', { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone })),
          reviewed: this.setReviewedStatus(release.Release.Reviewed),
          releaseStatus: this.setReleaseStatus(release.Release),
          team: this.getTeam(
            release.Release.PipelineName,
            release.Release.IsYaml
          ),
          releaseLink: release.Release.IsYaml
            ? this.getYamlReleaseLink(release.Release.AdoReleaseId)
            : this.getClassicReleaseLink(release.Release.AdoReleaseId),
          description: this.getDescriptions(release),
          changeFail: this.setChangeFail(release),
          isTransient: this.setIsTransient(release),
          reviewClassification: this.determineReviewClassification(
            release.Release
          ),
          releaseEnvironments: release.Release.ReleaseEnvironments
        });
      } catch (error) {
        console.error('Error processing release:', error);
      }
    });
  }

  determineReviewClassification(release: any): ReviewClassification {
    if (
      release.ReviewClassification === null ||
      release.ReviewClassification === undefined
    ) {
      return release.Reviewed
        ? ReviewClassification.Reviewed
        : ReviewClassification.NeedsReview;
    }

    // If reviewed, use the classification from the release object
    return release.ReviewClassification as ReviewClassification;
  }

  getDescriptions(release: any): ReleaseDetail[] {
    let descriptions: ReleaseDetail[] = [];
    if (release.Details) {
      release.Details.forEach(
        (x: { Description: string; DescriptionId: string }) =>
          descriptions.push({
            Description: x.Description,
            DescriptionId: x.DescriptionId,
          })
      );
    }
    return descriptions;
  }

  getDisplayDate(release: Release): string {
    let date = release.releaseStartedDateLocal;
    const formattedDate = date.toLocaleString('en-US', {
      year: '2-digit',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

    return formattedDate;
  }

  setChangeFail(release: any): boolean {
    if (
      release.Release.ChangeFail === null ||
      release.Release.ChangeFail === undefined
    ) {
      return this.setReleaseStatus(release.Release);
    }
    return release.Release.ChangeFail;
  }

  setReleaseStatus(release: any): boolean {
    if (release.Success === null) {
      return true;
    }
    return release.Success;
  }

  setReviewedStatus(reviewed: boolean | undefined | null): boolean {
    if (reviewed === undefined || reviewed === null) {
      return false;
    }

    return reviewed;
  }

  setIsTransient(release: any): boolean {
    if (
      release.Release.IsTransient === null ||
      release.Release.IsTransient === undefined
    ) {
      return false;
    }
    return release.Release.IsTransient;
  }

  getClassicReleaseLink(releaseId: string): string {
    return this.userProfileService.getClassicReleaseLink(
      this.defaultOrg?.accountName ?? '',
      this.defaultProject ?? '',
      releaseId
    );
  }

  getYamlReleaseLink(releaseId: string): string {
    return this.userProfileService.getYamlReleaseLink(
      this.defaultOrg?.accountName ?? '',
      this.defaultProject ?? '',
      releaseId
    );
  }

  getTeam(pipelineName: string, isYaml: boolean): string {
    let ownership: any[] = this.ownershipData || [];
    let propertyGroup = isYaml ? 'yamlPipelines' : 'releaseDefinitions';

    const result = ownership
      .filter((ownershipEntry) =>
        ownershipEntry[propertyGroup]?.some(
          (pipeline: { name: string }) => pipeline.name === pipelineName
        )
      )
      .map((ownershipEntry) => ownershipEntry.team);

    return result.length > 0 ? result[0] : '';
  }

  public rowClassRules: RowClassRules = {
    'highlight-row-green': (params: any) => {
      return this.getRowColor(params.data) === 'highlight-row-green';
    },
    'highlight-row-red':
      'data.releaseStatus === false && data.reviewed === false',
  };

  getRowColor(release: any): string {
    if (release.releaseStatus === true && release.reviewed === true) {
      return 'highlight-row-green';
    } else if (release.releaseStatus === false && release.reviewed === true) {
      return 'highlight-row-green';
    } else if (release.releaseStatus === false && release.reviewed === false) {
      return 'highlight-row-red';
    }
    return '';
  }

  setDefaultProject(): void {
    // Check if defaultOrg exists before trying to access its properties
    if (!this.defaultOrg || !this.defaultOrg.projects || !this.allProjects || this.allProjects.length === 0) {
      this.defaultProject = undefined;
      this.defaultProjectId = undefined;
      return;
    }

    const defaultProject = this.defaultOrg.projects.filter(
      (x) => x.toLowerCase() === this.environment.defaultProject.toLowerCase()
    )[0];

    if (!defaultProject) {
      this.defaultProject = undefined;
      this.defaultProjectId = undefined;
      return;
    }

    const matchingProject = this.allProjects.filter(
      (x) => x.name.toLowerCase() === defaultProject.toLowerCase()
    )[0];

    if (matchingProject) {
      this.defaultProject = defaultProject;
      this.defaultProjectId = matchingProject.id;
    } else {
      this.defaultProject = undefined;
      this.defaultProjectId = undefined;
    }
  }

  setDefaultOrg(): void {
    this.defaultOrg =
      this.availableAdoAccounts.filter(
        (x) =>
          x.accountName.toLowerCase() ===
          this.environment.defaultOrg.toLowerCase()
      )[0] ?? undefined;
  }

  /**
   * Gets available organizations and filters the available ADO accounts
   * @returns Observable that completes when orgs are fetched and accounts are filtered
   */
  getAvailableOrgs(): Observable<string[]> {
    return this.jitService.getAvailableOrgs().pipe(
      tap(orgs => {
        // Filter accounts based on available orgs
        this.availableAdoAccounts = this.availableAdoAccounts.filter(
          account => orgs.findIndex(org =>
            account.accountName.toLowerCase() === org.toLowerCase()
          ) >= 0
        );

        // Set default org after filtering accounts
        this.setDefaultOrg();
      })
    );
  }

  /**
   * Gets ownership data for the default organization
   * @returns Observable that completes when ownership data is fetched and processed
   */
  getOwnership(): Observable<OwnershipEntry[]> {
    if (!this.defaultOrg) {
      console.log('Default org not set');
      return throwError(() => new Error('Default org not set'));
    }

    this.loading = true;

    return this.userProfileService.getRepoOwnershipMaster(this.defaultOrg.accountName).pipe(
      tap((ownershipData: OwnershipEntry[]) => {
        this.ownershipData = ownershipData;

        // Extract unique teams and admins
        const allTeams: string[] = ownershipData
          .map((repo: OwnershipEntry) => repo.team)
          .filter((team: string) => team !== '');

        const allAdmins: string[] = ownershipData
          .map((repo: OwnershipEntry) => repo.admin)
          .filter((admin: string) => admin !== '');

        // Remove duplicates and sort alphabetically
        this.ownershipTeams = Array.from(new Set(allTeams))
          .sort((a, b) => a.localeCompare(b));

        this.ownershipAdmins = Array.from(new Set(allAdmins))
          .sort((a, b) => a.localeCompare(b));
      }),
      catchError(error => {
        console.error('Error fetching ownership data:', error);
        return throwError(() => new Error('Failed to fetch ownership data'));
      }),
      finalize(() => {
        // Only set loading to false if this is the only operation in progress
        // In production, would use a counter or more sophisticated loading state manager
      })
    );
  }
}
